<template>
<div class="odr-signature">
    <toggle :use="toggle">

        <dropdown :id="dropdownName" :fullscreen="true" :keep-alive="true">
            <div slot="content" class="odr-signature__dropdown">
                <div class="odr-signature__title" :style="`width: ${width}px !important;`">
                    <title-huge>
                        {{ $trl('aw.signature_title') }}
                    </title-huge>
                </div>
                <div class="odr-signature__canvas">
                    <canvas ref="canvas"
                            :width="width"
                            :height="height"
                            style="width: 100%"/>
                </div>

                <div class="odr-signature__sign-buttons">
                    <div class="odr-signature__sign-button">
                        <main-button width="100%" type="button" @click="save">
                            {{ $trl('aw.signature_save') }}
                        </main-button>
                    </div>
                    <div class="odr-signature__sign-button">
                        <main-button width="100%"
                                     clean="true"
                                     type="button"
                                     @click="clean">
                            {{ $trl('aw.signature_clear') }}
                        </main-button>
                    </div>
                    <div class="odr-signature__sign-button">
                        <main-button width="100%"
                                     clean="true"
                                     type="button"
                                     @click="cancel">
                            {{ $trl('aw.signature_cancel') }}
                        </main-button>
                    </div>
                </div>
            </div>
        </dropdown>

        <toggle-content name="sign">
            <img v-if="input"
                 class="odr-signature__image"
                 :class="{'odr-signature__image_progress': fileUpload_inProgress}"
                 :style="`width: ${width}px !important;`"
                 :src="sign">
            <div v-if="fileUpload_inProgress" :style="`width: ${width}px !important;`">
                <linear-progress/>
            </div>

            <text-regular v-if="fileUpload_failed" color="error">{{ $trl('aw.upload_image_invalid') }}</text-regular><br>
            <text-link v-if="fileUpload_failed"
                       bold="true"
                       color="error"
                       @click="retry">
                {{ $trl('aw.upload_image_retry') }}
            </text-link>

            <gap left="single">
                <text-link v-if="input"
                           bold="true"
                           @click="redraw">
                    {{ $trl('aw.signature_sign_clear') }}
                </text-link>
            </gap>
        </toggle-content>

        <toggle-content name="field">
            <gap top="single" bottom="single">
                <block type="input"
                       :stateId="stateId"
                       v-model="field"
                       :data-qa="dataQa + '.field'"
                       :lastpass="lastpass"
                       :focusable="true"
                       :focused="focused"
                       @blur="$emit('blur')"
                       @focus="$emit('focus')"
                       :title="$trl('aw.signature_field_title')"/>
            </gap>
            <canvas ref="fieldcanvas"
                    class="odr-signature__sign"
                    :width="fieldWidth"
                    :height="fieldHeight"/>
            <link href="https://fonts.googleapis.com/css?family=Caveat" rel="stylesheet">

            <div v-if="fileUpload_inProgress" :style="`width: ${width}px !important;`">
                <linear-progress/>
            </div>

            <template v-if="fileUpload_failed">
                <text-regular color="error">{{ $trl('aw.upload_image_invalid') }}</text-regular>
                <br>
                <text-link bold="true"
                           color="error"
                           @click="retry">
                    {{ $trl('aw.upload_image_retry') }}
                </text-link>
            </template>

            {{ $trl('aw.signature_draw') }} <br>
            <text-link @click="signOpen">
                {{ $trl('aw.signature_hand') }}
            </text-link>
        </toggle-content>
    </toggle>

    <div v-if="validation_showError && validation_error"
         class="odr-error-message"
         :class="{
             'odr-error-message_plain': $root.appConfig.mode !== 'chat'
         }">
        {{ validation_error }}
    </div>
</div>
</template>

<script>
import '@/scss/blocks/button-link.scss';
import '@/scss/blocks/error-message.scss';
import '@/scss/blocks/signature.scss';

import { debounce } from '@/utils/debounce';
import { dataURItoBlob } from '@/utils/blob';
import { showDropdown, hideDropdown } from '@/utils/dropdown';

export default {
    components: {
        'main-button': require('@clearwave/odoro-markup/lib/vue/main-button.vue').default,
        dropdown: require('@/vue/components/dropdown').default,
        block: require('@/vue/block').default,
        'linear-progress': require('@/vue/markup/linear-progress.vue').default,
        gap: require('@clearwave/odoro-markup/lib/vue/gap.vue').default,
        toggle: require('@clearwave/odoro-markup/lib/vue/toggle.vue').default,
        'toggle-content': require('@clearwave/odoro-markup/lib/vue/toggle-content.vue').default,
        'text-link': require('@clearwave/odoro-markup/lib/vue/text-link.vue').default,
        'text-regular': require('@clearwave/odoro-markup/lib/vue/text-regular.vue').default,
        'title-huge': require('@clearwave/odoro-markup/lib/vue/title-huge.vue').default,
    },
    mixins: [
        require('@/utils/mixins/validation').default,
        require('@/utils/mixins/focus').default,
        require('@/utils/mixins/file-upload').default,
    ],
    props: {
        id: {
            type: String,
            required: true,
        },
        lastpass: {
            type: Boolean,
        },
        stateId: {
            type: String,
            required: true,
        },
        dataQa: {
            type: String,
            default: '',
        },
        value: {},
        title: {
            type: String,
            default: '',
        },
        showByHand: {
            type: Boolean,
            default: true,
        },
        width: {
            type: Number,
            default: 320,
        },
        height: {
            type: Number,
            default: 240,
        },
        fieldWidth: {
            type: Number,
            default: 240,
        },
        fieldHeight: {
            type: Number,
            default: 40,
        },
        highlight: {
            type: Boolean,
        },
    },
    data () {
        return {
            dropdownName: 'signature.dropdown.' + this.id,
            input: this.value && this.value.base64 || '',
            sign: this.value && this.value.sign && this.value.base64 || '',
            fieldSign: this.value && !this.value.sign && this.value.base64 || '',
            field: this.value && this.value.text ? this.value.text : '',
            toggle: {
                sign: this.value && this.value.sign ? true : this.showByHand,
                field: this.value && this.value.sign ? false : !this.showByHand,
            },
        };
    },
    mounted () {
        this.$nextTick(() => {
            let canvas = document.createElement('canvas');
            canvas.setAttribute('style', 'position: absolute;top: 0;left: 0;width: 100%;');
            canvas.width = this.$refs.canvas.width;
            canvas.height = this.$refs.canvas.height;
            let canvasX;
            let canvasY;
            let scrollY;

            this.$refs.canvas.parentNode.appendChild(canvas);

            let context = canvas.getContext('2d');
            let started = false;

            let preventDefault = (event) => {
                event.preventDefault();
            };
            let disableScroll = () => {
                document.body.addEventListener('touchmove', preventDefault, { passive: false });
            };
            let enableScroll = () => {
                document.body.removeEventListener('touchmove', preventDefault);
            };

            let mousemove = event => {
                if (started) {
                    context.lineTo(event.pageX - canvasX, event.pageY - canvasY - scrollY);
                    context.stroke();
                }
            };

            let startDraw = event => {
                scrollY = window.scrollY;
                let rect = canvas.getBoundingClientRect();
                canvasX = rect.x;
                canvasY = rect.y;

                context.beginPath();
                context.moveTo(event.pageX - canvasX, event.pageY - canvasY - scrollY);
                started = true;
                disableScroll();
            };

            let stopDraw = event => {
                if (started) {
                    mousemove(event);
                    started = false;
                    this.$refs.canvas.getContext('2d').drawImage(canvas, 0, 0);
                    context.clearRect(0, 0, canvas.width, canvas.height);
                }
                enableScroll();
            };

            if (window.PointerEvent) {
                canvas.addEventListener('pointerdown', startDraw, false);
                canvas.addEventListener('pointermove', mousemove, false);
                window.addEventListener('pointerup', stopDraw, false);
            } else {
                canvas.addEventListener('touchstart', startDraw, false);
                canvas.addEventListener('touchmove', mousemove, false);
                canvas.addEventListener('touchend', stopDraw, false);
                canvas.addEventListener('mousedown', startDraw, false);
                canvas.addEventListener('mousemove', mousemove, false);
                window.addEventListener('mouseup', stopDraw, false);
            }

            this.$on('destroy', () => {
                window.removeEventListener('mouseup', stopDraw);
                window.removeEventListener('pointerup', stopDraw);
            });
        });
    },
    destroy () {
        this.$emit('destroy');
    },
    computed: {
        eInput () {
            if (this.uploadToken) {
                return this.fileUpload_succeed ? { token: this.uploadToken, base64: this.input, text: this.field, sign: !!this.toggle.sign } : '';
            } else {
                return this.input ? { base64: this.input, text: this.field, sign: !!this.toggle.sign } : '';
            }
        },
    },
    watch: {
        input () {
            this.validation_changed();

            if (this.uploadToken && this.input) {
                this.fileUpload_send(dataURItoBlob(this.input));
            }
        },
        eInput () {
            this.$emit('input', this.eInput);
        },
        toggle: {
            deep: true,
            handler () {
                if (this.toggle.sign) {
                    this.input = this.sign;
                } else {
                    this.updateInputOnType();
                }
            },
        },
        field: {
            handler () {
                if (this.toggle.field) {
                    this.$nextTick(() => {
                        this.drawField(this.field);
                        if (this.uploadToken) {
                            debounce(this.updateInputOnType, 1000);
                        } else {
                            this.updateInputOnType();
                        }
                    });
                }
            },
            immediate: true,
        },
    },
    methods: {
        retry () {
            this.fileUpload_send(dataURItoBlob(this.input));
        },
        redraw () {
            this.sign = '';
            this.toggle.sign = false;
            this.toggle.field = true;
        },
        signOpen () {
            if (this.sign) {
                this.toggle.sign = true;
                this.toggle.field = false;
            } else {
                showDropdown(this.dropdownName);
            }
        },
        drawField (field) {
            let ctx = this.$refs.fieldcanvas.getContext('2d');
            ctx.font = '30px Caveat';
            ctx.clearRect(0, 0, this.$refs.fieldcanvas.width, this.$refs.fieldcanvas.height);
            ctx.fillText(field, 0, this.fieldHeight - 10);
        },
        clean () {
            this.$refs.canvas.getContext('2d').clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
        },
        save () {
            this.sign = this.$refs.canvas.toDataURL();
            this.input = this.sign;
            this.toggle.sign = true;
            this.toggle.field = false;
            this.clean();
            hideDropdown(this.dropdownName);
        },
        cancel () {
            hideDropdown(this.dropdownName);
            this.clean();
        },
        uploadPhoto () {
            let file = this.$refs.file.files[0];
            let reader = new FileReader();

            reader.onloadend = () => {
                this.sign = reader.result;
            };

            if (file) {
                reader.readAsDataURL(file);
            }
        },
        updateInputOnType () {
            this.fieldSign = this.$refs.fieldcanvas.toDataURL();
            this.input = this.field ? this.fieldSign : '';
        },
        validation_isEmpty () {
            return !this.eInput;
        },
        validation_invalid () {
            return this.fileUpload_inProgress;
        },
        showDropdown,
        hideDropdown,
    },
};
</script>
